
import {defineComponent} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import usei18n from '@/core/util/composable/usei18n';
import useToast from '@/core/util/composable/useToast';
import useRoster, {
  parseYearMonth,
  ROSTER_SCOPE_MANAGER,
  ROSTER_STATUS_NOT_ELIGIBLE,
  ROSTER_STATUS_EN_ATTENTE,
  ROSTER_STATUS_COMPLETED,
  ROSTER_STATUS_SIGNEE,
  ROSTER_STATUS_CLOS,
  type RosterStatus,
  type RosterEntry,
} from '@/orangehrmForfaitJoursPlugin/util/composable/useRoster';

const MANAGER_SIGNING_PATH = '/forfaitJours/viewManagerSigning';

export default defineComponent({
  name: 'ManagerRoster',
  setup() {
    const {$t} = usei18n();
    const {error: showError} = useToast();
    const requestedMonth = new URLSearchParams(window.location.search).get(
      'month',
    );
    const initialMonth =
      requestedMonth && parseYearMonth(requestedMonth)
        ? requestedMonth
        : undefined;
    const roster = useRoster({scope: ROSTER_SCOPE_MANAGER, initialMonth});

    function statusToKey(status: RosterStatus): string {
      switch (status) {
        case ROSTER_STATUS_NOT_ELIGIBLE:
          return 'not_eligible';
        case ROSTER_STATUS_EN_ATTENTE:
          return 'en_attente';
        case ROSTER_STATUS_COMPLETED:
          return 'completed';
        case ROSTER_STATUS_SIGNEE:
          return 'signee';
        case ROSTER_STATUS_CLOS:
          return 'clos';
        default:
          return 'not_eligible';
      }
    }

    function onMonthChange(): void {
      // Reset to page 1 before reloading so the new month starts
      // at the first page.
      roster.currentPage.value = 1;
      void roster.load();
    }

    /**
     * A row is clickable only when the declaration is SUBMITTED
     * (manager can sign) or SIGNEE/SIGNED (manager can download PDF).
     * NOT_ELIGIBLE / EN_ATTENTE rows have no signing action.
     * SIGNEE rows trigger PDF download directly.
     * COMPLETED/SUBMITTED rows navigate to the signing page.
     */
    function isRowClickable(entry: RosterEntry): boolean {
      return (
        entry.declarationId !== null &&
        (entry.status === ROSTER_STATUS_COMPLETED ||
          entry.status === ROSTER_STATUS_SIGNEE)
      );
    }

    async function downloadPdf(declarationId: number): Promise<void> {
      // @ts-expect-error: appGlobal is not in window object by default
      const baseUrl = window.appGlobal.baseUrl;
      const pdfHttp = new APIService(
        baseUrl,
        `/api/v2/forfait-jours/declarations/${declarationId}/pdf`,
      );
      try {
        const response = await pdfHttp.request({
          method: 'GET',
          params: {kind: 'signed'},
        });
        const contentBase64 = response?.data?.data?.contentBase64;
        if (!contentBase64) {
          throw new Error('Signed PDF content is missing');
        }
        const byteStr = atob(contentBase64);
        const byteArray = new Uint8Array(byteStr.length);
        for (let i = 0; i < byteStr.length; i++) {
          byteArray[i] = byteStr.charCodeAt(i);
        }
        const blob = new Blob([byteArray], {type: 'application/pdf'});
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = response.data.data.filename || 'declaration.pdf';
        a.click();
        URL.revokeObjectURL(url);
      } catch {
        void showError({
          title: $t('general.error'),
          message: $t('forfait_jours.pdf_download_error'),
        });
      }
    }

    function onRowClick(entry: RosterEntry): void {
      if (!isRowClickable(entry) || entry.declarationId === null) {
        return;
      }
      // @ts-expect-error: appGlobal is not in window object by default
      const baseUrl = window.appGlobal.baseUrl;

      if (entry.status === ROSTER_STATUS_SIGNEE) {
        void downloadPdf(entry.declarationId);
        return;
      }

      // SUBMITTED → navigate to signing page
      const yearMonth = roster.month.value;
      const [yearStr, monthStr] = yearMonth.split('-');
      const params = new URLSearchParams({
        declarationId: String(entry.declarationId),
        employeeName: entry.fullName,
        year: yearStr,
        month: monthStr,
      });
      window.location.href = `${baseUrl}${MANAGER_SIGNING_PATH}?${params.toString()}`;
    }

    void roster.load();

    return {
      month: roster.month,
      entries: roster.entries,
      isLoading: roster.isLoading,
      isEmpty: roster.isEmpty,
      error: roster.error,
      currentPage: roster.currentPage,
      pages: roster.pages,
      showPaginator: roster.showPaginator,
      statusToKey,
      onMonthChange,
      isRowClickable,
      onRowClick,
    };
  },
});
